import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { Id } from '../../convex/_generated/dataModel'
import { Header } from '../components/layout/Header'
import { Button } from '../components/common/Button'
import { MedicationCard } from '../components/medications/MedicationCard'
import { MedicationFormModal } from '../components/medications/MedicationFormModal'
import { MedicationHistory } from '../components/medications/MedicationHistory'
import { VacationPauseBanner } from '../components/medications/VacationPauseBanner'
import { VacationPauseModal } from '../components/medications/VacationPauseModal'
import { NextCallBanner } from '../components/medications/NextCallBanner'
import { MedicationCalendar } from '../components/medications/MedicationCalendar'
import { DayDetailModal } from '../components/medications/DayDetailModal'

interface EditableMedication {
  _id: Id<'medications'>
  name: string
  timeSlot: string
  specificTime: string
  notes?: string
  frequency?: string
  frequencyDayOfWeek?: number
  frequencyOrdinal?: string
  frequencyDayOfMonth?: number
  oneTimeDate?: string
  remindDayBefore?: boolean
}

/**
 * MedicationFeature - Medication Reminders feature detail page with calendar
 * Desktop: Two-column layout (calendar left, medications right)
 * Mobile: Single column stacked layout
 */
export function MedicationFeature() {
  const { t } = useTranslation()
  const { lovedOneId } = useParams<{ lovedOneId: string }>()
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const [showFormModal, setShowFormModal] = useState(false)
  const [editingMedication, setEditingMedication] = useState<EditableMedication | undefined>()
  const [showHistory, setShowHistory] = useState(false)
  const [showPauseModal, setShowPauseModal] = useState(false)

  // 005: Get medications with auth
  const medications = useQuery(
    api.medications.getByLovedOne,
    lovedOneId ? { lovedOneId: lovedOneId as Id<'lovedOnes'> } : 'skip'
  )

  // 005: Get active vacation pause
  const activePause = useQuery(
    api.vacationPauses.getActive,
    lovedOneId ? { lovedOneId: lovedOneId as Id<'lovedOnes'> } : 'skip'
  )

  // 004-1: Get loved one detail for phone number warning
  const detail = useQuery(
    api.dashboard.getLovedOneDetail,
    lovedOneId ? { lovedOneId: lovedOneId as Id<'lovedOnes'> } : 'skip'
  )

  // 004-1: Get next scheduled call
  const nextCall = useQuery(
    api.scheduling.getNextScheduledCall,
    lovedOneId ? { lovedOneId: lovedOneId as Id<'lovedOnes'> } : 'skip'
  )

  // 004-1: Get adherence history for current month
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).getTime()

  const adherenceHistory = useQuery(
    api.scheduling.getAdherenceHistory,
    lovedOneId
      ? {
          lovedOneId: lovedOneId as Id<'lovedOnes'>,
          startDate: startOfMonth,
          endDate: endOfMonth,
        }
      : 'skip'
  )

  // 004-1: Get day detail when a date is selected
  const dayDetail = useQuery(
    api.scheduling.getDayDetail,
    selectedDate && lovedOneId
      ? {
          lovedOneId: lovedOneId as Id<'lovedOnes'>,
          date: selectedDate,
        }
      : 'skip'
  )

  // 005: Create baseline snapshot
  const createBaseline = useMutation(api.medicationSnapshots.createBaseline)

  // Create baseline snapshot on mount if medications exist
  useEffect(() => {
    if (lovedOneId && medications && medications.length > 0) {
      createBaseline({ lovedOneId: lovedOneId as Id<'lovedOnes'> })
    }
  }, [lovedOneId, medications?.length])

  const handleEdit = (medication: EditableMedication) => {
    setEditingMedication(medication)
    setShowFormModal(true)
  }

  const handleAddNew = () => {
    setEditingMedication(undefined)
    setShowFormModal(true)
  }

  const handleCloseModal = () => {
    setShowFormModal(false)
    setEditingMedication(undefined)
  }

  const containerStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const contentWrapperStyles: React.CSSProperties = {
    padding: '1.5rem 1rem',
    maxWidth: '1200px',
    margin: '0 auto',
  }

  const headerRowStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1.5rem',
  }

  const backLinkStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#0066cc',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: 500,
    minHeight: '44px',
    padding: '0.5rem 1rem',
    backgroundColor: '#f0f7ff',
    borderRadius: '8px',
    border: '1px solid #cce5ff',
  }

  const titleStyles: React.CSSProperties = {
    fontSize: 'clamp(20px, 4vw, 28px)',
    fontWeight: 700,
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: 0,
  }

  const statusBadgeStyles = (hasActive: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: hasActive ? '#dcfce7' : '#f3f4f6',
    color: hasActive ? '#16a34a' : '#9ca3af',
    marginBottom: '1.5rem',
  })

  const twoColumnStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 1fr)',
    gap: '2rem',
    alignItems: 'start',
  }

  const columnStyles: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  }

  const sectionTitleStyles: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '1rem',
    marginTop: 0,
  }

  const cardStyles: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  }

  const emptyStateStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#9ca3af',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  }

  const loadingStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '2rem',
    color: '#666',
  }

  const warningStyles: React.CSSProperties = {
    padding: '0.75rem 1rem',
    backgroundColor: '#fef3c7',
    border: '1px solid #f59e0b',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  }

  if (!lovedOneId) {
    return (
      <div style={containerStyles}>
        <Header />
        <div style={contentWrapperStyles}>
          <p>{t('errors.generic')}</p>
        </div>
      </div>
    )
  }

  if (medications === undefined) {
    return (
      <div style={containerStyles}>
        <Header />
        <div style={contentWrapperStyles}>
          <p style={loadingStyles}>{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Sort medications chronologically: morning -> afternoon -> evening -> bedtime, then by specificTime
  const timeSlotOrder: Record<string, number> = { morning: 0, afternoon: 1, evening: 2, bedtime: 3 }
  const toMinutes = (timeStr: string) => {
    const m = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i)
    if (!m) return 0
    let h = parseInt(m[1], 10)
    const min = parseInt(m[2], 10)
    const pm = m[3].toUpperCase() === 'PM'
    if (h === 12) h = 0
    return (pm ? h + 12 : h) * 60 + min
  }
  const sortMeds = (meds: typeof medications) =>
    [...meds].sort((a, b) => {
      const slotDiff = (timeSlotOrder[a.timeSlot] ?? 99) - (timeSlotOrder[b.timeSlot] ?? 99)
      if (slotDiff !== 0) return slotDiff
      return toMinutes(a.specificTime ?? '') - toMinutes(b.specificTime ?? '')
    })

  // Group medications by frequency
  const frequencyGroupOrder = [
    'daily',
    'weekly',
    'biweekly',
    'monthly_day_of_week',
    'monthly_date',
    'one_time',
  ]
  const frequencyGroupLabels: Record<string, string> = {
    daily: t('medicationManagement.schedule.daily'),
    weekly: t('medicationManagement.schedule.weekly'),
    biweekly: t('medicationManagement.schedule.biweekly'),
    monthly_day_of_week: t('medicationManagement.schedule.monthlyDayOfWeek'),
    monthly_date: t('medicationManagement.schedule.monthlyDate'),
    one_time: t('medicationManagement.schedule.oneTime'),
  }
  const groupedMedications = frequencyGroupOrder
    .map(freq => ({
      frequency: freq,
      label: frequencyGroupLabels[freq],
      meds: sortMeds(medications.filter(m => ((m as any).frequency || 'daily') === freq)),
    }))
    .filter(g => g.meds.length > 0)

  const hasActiveMedications = medications.length > 0

  return (
    <div style={containerStyles}>
      <Header />
      <div style={contentWrapperStyles}>
        <Link to={`/dashboard/${lovedOneId}`} style={backLinkStyles}>
          &larr; {t('medicationManagement.backToDashboard')}
        </Link>

        <div style={headerRowStyles}>
          <h1 style={titleStyles}>{t('medicationManagement.title')}</h1>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {hasActiveMedications && (
              <>
                {!activePause && (
                  <Button size="small" variant="outline" onClick={() => setShowPauseModal(true)}>
                    {t('medicationManagement.vacationPause.pauseCalls')}
                  </Button>
                )}
                <Button size="small" variant="outline" onClick={() => setShowHistory(true)}>
                  {t('medicationManagement.history.title')}
                </Button>
                <Button size="small" onClick={handleAddNew}>
                  {t('medicationManagement.addMedication')}
                </Button>
              </>
            )}
          </div>
        </div>

        <div style={statusBadgeStyles(hasActiveMedications)}>
          {hasActiveMedications ? t('dashboard.active') : t('dashboard.notSetUp')}
        </div>

        {/* Warning if no phone number (from 004-1) */}
        {detail && !detail.lovedOne.phoneNumber && (
          <div style={warningStyles}>
            <span style={{ fontSize: '16px' }}>&#9888;&#65039;</span>
            <span style={{ fontSize: '14px', color: '#92400e' }}>
              {t(
                'medications.noPhoneWarning',
                'No phone number set. Medication reminders cannot be sent until a phone number is added in Settings.'
              )}
            </span>
          </div>
        )}

        {/* Next Call Banner - full width (from 004-1) */}
        {hasActiveMedications && <NextCallBanner nextCall={nextCall || null} />}

        {activePause && !showHistory && <VacationPauseBanner pause={activePause} />}

        {showHistory ? (
          <MedicationHistory
            lovedOneId={lovedOneId as Id<'lovedOnes'>}
            onClose={() => setShowHistory(false)}
          />
        ) : !hasActiveMedications ? (
          <div style={emptyStateStyles}>
            <p style={{ fontSize: '48px', marginBottom: '1rem' }}>&#128138;</p>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '0.5rem' }}>
              {t('medicationManagement.noMedications')}
            </p>
            <p style={{ fontSize: '14px', marginBottom: '1.5rem' }}>
              {t('medicationManagement.noMedicationsHint')}
            </p>
            <Button onClick={handleAddNew}>{t('medicationManagement.addFirst')}</Button>
          </div>
        ) : (
          /* Two-column layout: calendar left, medications right (merged from 004-1 and 005) */
          <div style={twoColumnStyles} className="medication-grid">
            {/* Left Column: Adherence Calendar (from 004-1) */}
            <div style={columnStyles}>
              <h2 style={sectionTitleStyles}>
                {t('medications.adherenceCalendar', 'Adherence Calendar')}
              </h2>
              <MedicationCalendar
                adherenceHistory={adherenceHistory || []}
                onDayClick={setSelectedDate}
              />
            </div>

            {/* Right Column: Medications List grouped by frequency (from 005) */}
            <div style={columnStyles}>
              <h2 style={sectionTitleStyles}>
                {t('medications.yourMedications', 'Your Medications')}
              </h2>
              {groupedMedications.map(group => (
                <div key={group.frequency}>
                  <h3
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '0.5rem',
                      marginTop: '1rem',
                    }}
                  >
                    {group.label}
                  </h3>
                  <div style={cardStyles}>
                    {group.meds.map(med => (
                      <MedicationCard key={med._id} medication={med as any} onEdit={handleEdit} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Day Detail Modal (from 004-1) */}
        <DayDetailModal
          isOpen={!!selectedDate}
          onClose={() => setSelectedDate(null)}
          date={selectedDate || ''}
          reminders={dayDetail?.reminders || []}
        />

        {/* Responsive styles for two-column layout */}
        <style>{`
          @media (max-width: 768px) {
            .medication-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>

      <MedicationFormModal
        isOpen={showFormModal}
        onClose={handleCloseModal}
        lovedOneId={lovedOneId as Id<'lovedOnes'>}
        medication={editingMedication}
      />

      <VacationPauseModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        lovedOneId={lovedOneId as Id<'lovedOnes'>}
      />
    </div>
  )
}
